import {
  PayableActionTypes,
  PayableStepActionTypes,
} from "#components/PayableWorkflow/Context/constants";
import { createReactContext, createReactDispatchContext } from "#utils/reactContext";
import {
  PayableData,
  PayableReducerAction,
  PayableStepAction,
  PayableWorkflowData,
} from "./types";

export const initialPayableState: PayableData = {
  arenaId: undefined,
  arenaName: undefined,
  payeeId: undefined,
  payeeName: undefined,
  payeeType: undefined,
  invoiceDocuments: undefined,
  supportingDocuments: undefined,
  entityId: undefined,
  entityName: undefined,
  entityAchCompanyIdPresent: undefined,
  entityPaymentCapabilities: undefined,
  invoiceNumber: undefined,
  invoiceDate: undefined,
  dueDate: undefined,
  description: undefined,
  paymentAmount: undefined,
  paymentDate: undefined,
  paymentDescription: undefined,
};

/**
 * Reducer for managing payable data state
 */
export const payableDataReducer = (
  state: PayableData,
  action: PayableReducerAction,
): PayableData => {
  switch (action.type) {
    case PayableActionTypes.SET_CLIENT: {
      return {
        ...state,
        arenaId: action.payload.arenaId,
        arenaName: action.payload.arenaName,
      };
    }
    case PayableActionTypes.SET_PAYEE: {
      return {
        ...state,
        payeeId: action.payload.payeeId,
        payeeName: action.payload.payeeName,
        payeeType: action.payload.payeeType,
        payeeStatus: action.payload.payeeStatus,
      };
    }
    case PayableActionTypes.SET_DOCUMENTS: {
      return {
        ...state,
        invoiceDocuments: action.payload.invoiceDocuments,
        supportingDocuments: action.payload.supportingDocuments,
      };
    }
    case PayableActionTypes.SET_ENTITY: {
      return {
        ...state,
        entityId: action.payload.entityId,
        entityName: action.payload.entityName,
        entityAchCompanyIdPresent: action.payload.entityAchCompanyIdPresent,
        entityPaymentCapabilities: action.payload.entityPaymentCapabilities,
      };
    }
    case PayableActionTypes.SET_INVOICE_DETAILS: {
      // Invoice details collected in Step 1 (vendor only) - no invoiceAmount
      return {
        ...state,
        invoiceNumber: action.payload.invoiceNumber,
        invoiceDate: action.payload.invoiceDate,
        dueDate: action.payload.dueDate,
        description: action.payload.description,
      };
    }
    case PayableActionTypes.SET_PAYMENT_DETAILS: {
      return {
        ...state,
        paymentAmount: action.payload.paymentAmount, // Used for amount in both payable and payment
        paymentDate: action.payload.paymentDate,
        paymentDescription: action.payload.paymentDescription,
      };
    }
    default: {
      return state;
    }
  }
};

// Data context and dispatch
const [usePayableDataContext, PayableDataContextProvider] = createReactContext<PayableData>();

export { usePayableDataContext, PayableDataContextProvider };

const [usePayableDataDispatch, PayableDataDispatchProvider] =
  createReactDispatchContext<PayableReducerAction>();

export { usePayableDataDispatch, PayableDataDispatchProvider };

export const initialWorkflowState: PayableWorkflowData = {
  stepNumber: 0,
};

/**
 * Reducer for managing workflow step navigation
 */
export const payableStepReducer = (
  state: PayableWorkflowData,
  action: PayableStepAction,
): PayableWorkflowData => {
  switch (action.type) {
    case PayableStepActionTypes.INCREMENT_STEP: {
      // Max 2 steps (0 and 1)
      if (state.stepNumber >= 1) {
        return state;
      }
      return {
        ...state,
        stepNumber: state.stepNumber + 1,
      };
    }
    case PayableStepActionTypes.DECREMENT_STEP: {
      if (state.stepNumber > 0) {
        return {
          ...state,
          stepNumber: state.stepNumber - 1,
        };
      }
      return state;
    }
    default: {
      return state;
    }
  }
};

// Workflow context and dispatch
const [usePayableWorkflowContext, PayableWorkflowContextProvider] =
  createReactContext<PayableWorkflowData>();

export { usePayableWorkflowContext, PayableWorkflowContextProvider };

const [usePayableWorkflowDispatch, PayableWorkflowDispatchProvider] =
  createReactDispatchContext<PayableStepAction>();

export { usePayableWorkflowDispatch, PayableWorkflowDispatchProvider };
